import { CommonModule } from '@angular/common';
import { Provider } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import {
  AccessService,
  CM_UI_COMPOUND_FILTER_OBJECT_NAMES_PROVIDER,
  CM_UI_COMPOUND_FILTER_PREDEFINED_FILTERS,
  CM_UI_NAVBAR_LOGO_URL,
  CM_UI_NAVBAR_SETTINGS,
  CmAccessModuleModule,
  CmUiCompoundFilterModule,
  CmUiFormModule,
  CmUiLoaderModule,
  CmUiNavbarModule,
  CmUiTableModule,
  UiTopBarModule,
  CmUiTableContainerComponent,
  CmUiBasicsModule,
  CmUiCompoundFilterObjectNameOption,
} from '@cm/ui-modules';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../environments/environment';
import * as fromComponents from './components';
import { DataListService } from './services/data-list.service';
import { CookieService } from 'ngx-cookie-service';
import { InactivityMonitorService } from './services/inactivitymonitor.service';
import { FilterInitiatorService } from './services/filter-initiator.service';
import { MENU_PROVIDER } from './config/menu.config';

// Re-export from dedicated shared-imports.ts to keep SHARED_IMPORTS free of
// workflow-module dependencies (prevents a circular-import TDZ bug).
export { MATERIAL_MODULES, CM_UI_MODULES, SHARED_IMPORTS } from './shared-imports';
import { SHARED_IMPORTS } from './shared-imports';

// Ensure compound filter module is properly imported
export { CmUiCompoundFilterModule } from '@cm/ui-modules';

// Export individual components for direct import
export { LayoutComponent } from './components/layout/layout.component';
export { DataListViewComponent } from './components/data-list-view/data-list-view.component';
export { TimezoneModalComponent } from './components/timezone-modal/timezone-modal.component';
export { LabelButtonComponent } from './components/label-button/label-button.component';
export { OrgStructureDropdownComponent } from './components/org-structure-dropdown/org-structure-dropdown.component';
export { CmIconButtonComponent } from './components/icon-button/icon-button.component';
export { CmIconDropdownComponent, IconDropdownOption } from './components/icon-dropdown/icon-dropdown.component';
export { DigitsOnlyDirective } from './directives/digits-only.directive';

// Export workflow automator components
export { ConfigurationFieldControlComponent } from '../modules/workflow/workflow-element-configuration/components/configuration-field-control/configuration-field-control.component';
export { DynamicVariablePickerComponent } from '../modules/workflow/workflow-element-configuration/components/dynamic-variable-picker/dynamic-variable-picker.component';
export { FormFieldErrorComponent } from '../modules/workflow/workflow-element-configuration/components/form-field-error/form-field-error.component';
export { TableInputComponent } from '../modules/workflow/workflow-element-configuration/components/table-input/table-input.component';
export { ObjectLabelComponent } from '../modules/workflow/workflow-element-configuration/components/object-label/object-label.component';
export { UniversalFormControlComponent } from '../modules/workflow/workflow-element-configuration/components/universal-form-control/universal-form-control.component';
export { CompoundFilterControlComponent } from '../modules/workflow/workflow-element-configuration/components/compound-filter-control/compound-filter-control.component';
export { QueryFilterControlComponent } from '../modules/workflow/workflow-element-configuration/components/query-filter-control/query-filter-control.component';
export { ObjectScalarControlComponent } from '../modules/workflow/workflow-element-configuration/workflow-elements/webhook/components/object-scalar-control/object-scalar-control.component';
// Import workflow automator components
import { ConfigurationFieldControlComponent } from '@element/components/configuration-field-control/configuration-field-control.component'
import { DynamicVariablePickerComponent } from '../modules/workflow/workflow-element-configuration/components/dynamic-variable-picker/dynamic-variable-picker.component';
import { FormFieldErrorComponent } from '../modules/workflow/workflow-element-configuration/components/form-field-error/form-field-error.component';
import { TableInputComponent } from '../modules/workflow/workflow-element-configuration/components/table-input/table-input.component';
import { ObjectLabelComponent } from '../modules/workflow/workflow-element-configuration/components/object-label/object-label.component';
import { UniversalFormControlComponent } from '../modules/workflow/workflow-element-configuration/components/universal-form-control/universal-form-control.component';
import { CompoundFilterControlComponent } from '../modules/workflow/workflow-element-configuration/components/compound-filter-control/compound-filter-control.component';
import { QueryFilterControlComponent } from '../modules/workflow/workflow-element-configuration/components/query-filter-control/query-filter-control.component';
import { DigitsOnlyDirective } from './directives/digits-only.directive';
import { ObjectScalarControlComponent } from '../modules/workflow/workflow-element-configuration/workflow-elements/webhook/components/object-scalar-control/object-scalar-control.component';

const AUTOMATOR_SHARED_COMPONENTS = [
  ConfigurationFieldControlComponent,
  DynamicVariablePickerComponent,
  FormFieldErrorComponent,
  TableInputComponent,
  ObjectLabelComponent,
  UniversalFormControlComponent,
  CompoundFilterControlComponent,
  QueryFilterControlComponent,
  ObjectScalarControlComponent,
];

// Configuration imports for workflow element components (excludes components that create circular dependencies)
const AUTOMATOR_CONFIGURATION_COMPONENTS = [
  // ConfigurationFieldControlComponent removed to prevent circular dependency
  DynamicVariablePickerComponent,
  FormFieldErrorComponent,
  TableInputComponent,
  ObjectLabelComponent,
  UniversalFormControlComponent,
  CompoundFilterControlComponent,
  QueryFilterControlComponent,
];

export const SHARED_AUTOMATOR_IMPORTS = [
  ...SHARED_IMPORTS,
  ...AUTOMATOR_SHARED_COMPONENTS,
  DigitsOnlyDirective,
];


// Default object names for compound filter - can be overridden per component
const DEFAULT_COMPOUND_FILTER_OBJECT_NAMES: CmUiCompoundFilterObjectNameOption[] = [
  { label: 'Contact', value: 'Contact' },
  { label: 'Representative', value: 'Representative' },
  { label: 'Workplace', value: 'Workplace' },
  { label: 'Group', value: 'Group' },
  { label: 'Specialization', value: 'Specialization' },
  { label: 'CustomFieldDefinition', value: 'CustomFieldDefinition' },
  { label: 'Consent', value: 'Consent' },
  { label: 'MessageCampaign', value: 'MessageCampaign' },
  { label: 'Meeting', value: 'Meeting' },
  { label: 'MeetingEvent', value: 'MeetingEvent' },
];

// Function to create table-specific providers with translation support
export function provideTableProviders(
  predefinedFilters?: any[],
  filters: CmUiCompoundFilterObjectNameOption[] = DEFAULT_COMPOUND_FILTER_OBJECT_NAMES,
  enableTranslations: boolean = true
): Provider[] {
  const providers: Provider[] = [
    MENU_PROVIDER,
    {
      provide: CM_UI_COMPOUND_FILTER_OBJECT_NAMES_PROVIDER,
      useFactory: (filterInitiatorService: FilterInitiatorService) => {
        const finalObjectNames = filters;
        if (enableTranslations) {
          filterInitiatorService.initializeFilterTranslations(finalObjectNames, predefinedFilters);
        }
        return finalObjectNames;
      },
      deps: [FilterInitiatorService],
    },
    {
      provide: CM_UI_NAVBAR_LOGO_URL,
      useValue: {
        logoUrl: environment.highp_logo,
        hrefUrl: null,
        routerLink: '/dashboard'
      },
    },
    {
      provide: CM_UI_NAVBAR_SETTINGS,
      useValue: {
        version: environment.VERSION,
        appName: environment.appName,
      },
    },
  ];

  // Only provide predefined filters if they exist and are not empty
  if (predefinedFilters && predefinedFilters.length > 0) {
    providers.push({
      provide: CM_UI_COMPOUND_FILTER_PREDEFINED_FILTERS,
      useValue: predefinedFilters,
    });
  }

  return providers;
}

// Shared imports for components using cm-ui-table
export const SHARED_TABLE_IMPORTS = SHARED_IMPORTS;

export function provideSharedServices(): Provider[] {
  return [
    DataListService,
    AccessService,
    CookieService,
    InactivityMonitorService,
    TranslateService,
    {
      provide: CM_UI_NAVBAR_SETTINGS,
      useValue: {
        version: environment.VERSION,
        appName: environment.appName,
      },
    },
    {
      provide: CM_UI_NAVBAR_LOGO_URL,
      useValue: {
        logoUrl: environment.highp_logo,
        routerLink: ['','dashboard'],
      },
    },
  ];
}
